import { ReactNode, useEffect, useMemo, useState } from 'react'
import { Button, Icon, LoadingAnimation, Text } from '@/components'

import { DotIcon } from 'lucide-react'
import Tabs from '@/components/Tabs'
import { match, P } from 'ts-pattern'
import {
  DeviceEligibility,
  DeviceIneligibility,
  selectProgramDeviceLimits,
  selectProgramRewardDeviceLimits,
} from '@/reducers/programsNew'
import {
  default as useViewModel,
  UseViewModelProps,
} from '@/app/pages/multi-program-enrollment/device-selection/viewModel'
import { AnimatePresence, motion } from 'motion/react'
import { VehicleEligibilityRequirement } from '@/types/programs-new/vehicle-requirements'
import { AlertMessage } from 'app-components'
import DeviceLimitHelpers from '@/app/pages/multi-program-enrollment/device-selection/DeviceLimitHelpers'
import { useAppSelector } from '@/hooks'
import { useMultiProgramRouteContext } from '@/app/pages/multi-program-enrollment'
import DeviceLimitsDisplay from '@/app/pages/multi-program-enrollment/device-selection/device-limits-display'
import Checkbox from '@/components/forms/checkbox'
import DynamicProgramRewardsCard from '@/components/ProgramRewards/dynamic-program-rewards-card'
import { getVehicleDisplayName } from '@/utils/CarUtils'
import ExpandFade from '@/components/Animations/ExpandFade/ExpandFade'
import { DeviceLimits } from '@/types/programs-new/programs-new'
import { DeviceType } from '@/app/components/DeviceSelectionGallery/DeviceSelectionGallery.types'
import MessageBox from '@/app/components/MessageBox/MessageBox'

enum TabName {
  INELIGIBLE = 'Ineligible',
  ELIGIBLE = 'Eligible',
}

const TAB_KEY = 'program-select-devices-tabs'

export default function UtilityProgramDeviceSelectionPage() {
  const {
    selectedEligibleChargerIds,
    selectedEligibleThermostatIds,
    selectedEligibleVehicleIds,
    toggleVehicleEligibility,
    toggleThermostatEligibility,
    toggleChargerEligibility,
    initialTabIndex,
    deviceIneligibility,
    deviceEligibility,
    onContinueClick,
    ineligibleDeviceCount,
    eligibleDeviceCount,
    onAddDevice,
    deviceIncentives,
    deviceLimitsResult,
    selectedDevicesMeetLimits,
    rewardsByCategory,
    deviceLimits,
    rewardDeviceLimits,
    utilityProgramId,
  } = useViewModel()

  const tabs = [
    `Ineligible (${ineligibleDeviceCount})`,
    `Eligible (${eligibleDeviceCount})`,
  ]
  const selectedDeviceCount = useMemo(
    () =>
      selectedEligibleChargerIds.length +
      selectedEligibleThermostatIds.length +
      selectedEligibleVehicleIds.length,
    [
      selectedEligibleVehicleIds,
      selectedEligibleThermostatIds,
      selectedEligibleChargerIds,
    ],
  )
  const [tabIndex, setTabIndex] = useState(initialTabIndex)
  // initialTabIndex is derived from device data that arrives after first render, so it can flip
  // once loading finishes. Tabs re-syncs its own store on that change (which moves the tab bar and
  // the visible panel); mirror it here so the footer button doesn't get stranded on the tab the
  // user is no longer looking at.
  useEffect(() => {
    setTabIndex(initialTabIndex)
  }, [initialTabIndex])
  const selectedTabName = useMemo(
    () =>
      match(tabIndex)
        .with(0, () => TabName.INELIGIBLE)
        .with(1, () => TabName.ELIGIBLE)
        .otherwise(() => TabName.INELIGIBLE),
    [tabIndex],
  )
  // prevent loading animation from flickering
  const loadingAnimation = useMemo(
    () => (
      <div className={'flex justify-center items-center h-full'}>
        <LoadingAnimation type={'falling'} />
      </div>
    ),
    [],
  )
  const headerContent =
    'Connect your device(s) for rewards and smarter device management.'

  const noDevicesAtAll =
    ineligibleDeviceCount === 0 && eligibleDeviceCount === 0

  return (
    <div className="flex flex-col gap-4 justify-between h-full">
      <div className="flex flex-col gap-4">
        <section className="flex flex-col gap-1">
          <Text variant="h3">Connect Devices</Text>
          <Text variant="body2">{headerContent}</Text>
        </section>
        {(rewardsByCategory?.enrollment ||
          rewardsByCategory?.participation) && (
          <div className="w-full flex items-center justify-center">
            <DynamicProgramRewardsCard
              rewardsByCategory={rewardsByCategory}
              devicesSelected={{
                vehicleCount: selectedEligibleVehicleIds.length,
                thermostatCount: selectedEligibleThermostatIds.length,
                chargerCount: selectedEligibleChargerIds.length,
              }}
              rewardDeviceLimits={rewardDeviceLimits}
            />
          </div>
        )}
        <section>
          <DeviceLimitsDisplay
            utilityProgramId={utilityProgramId}
            deviceLimitsResult={deviceLimitsResult}
            deviceLimits={deviceLimits}
          />
        </section>
        {deviceLimitsResult.limitsMet && (
          <section className="flex flex-col items-center space-y-6">
            <Tabs.Tabs
              initialTabIndex={initialTabIndex}
              tabKey={TAB_KEY}
              labels={tabs}
              tabBarAttributes={{
                className:
                  'max-w-none bg-themed-white rounded-[20px] shadow-[0_0_10px_rgba(0,0,0,0.08)]',
              }}
              onTabChange={(index) => {
                Tabs.setActiveTabIndex(TAB_KEY, index)
                setTabIndex(index)
              }}
            />
            <Tabs.Content tabKey="program-select-devices-tabs">
              <div className={'flex justify-start w-full'}>
                <TabContentRoot
                  body={
                    <>
                      {match({ deviceIneligibility, noDevicesAtAll })
                        .with(
                          {
                            deviceIneligibility: P.not(P.nullish),
                            noDevicesAtAll: true,
                          },
                          () => <NoDevicesAtAllContent />,
                        )
                        .with(
                          {
                            deviceIneligibility: P.not(P.nullish),
                            noDevicesAtAll: false,
                          },
                          () => (
                            <IneligibleDevices
                              deviceIneligibility={deviceIneligibility}
                              ineligibleDeviceCount={ineligibleDeviceCount}
                              onAddDevice={onAddDevice}
                            />
                          ),
                        )
                        .with(
                          { deviceIneligibility: P.nullish },
                          () => loadingAnimation,
                        )
                        .otherwise(() => null)}
                    </>
                  }
                />
              </div>
              <div className={'flex justify-start w-full [&>*]:select-none'}>
                <TabContentRoot
                  body={
                    <>
                      {match({ deviceIneligibility, noDevicesAtAll })
                        .with(
                          {
                            deviceIneligibility: P.not(P.nullish),
                            noDevicesAtAll: true,
                          },
                          () => <NoDevicesAtAllContent />,
                        )
                        .with(
                          {
                            deviceIneligibility: P.not(P.nullish),
                            noDevicesAtAll: false,
                          },
                          () => (
                            <EligibleDevices
                              eligibleDeviceCount={eligibleDeviceCount}
                              deviceEligibility={deviceEligibility}
                              selectedEligibleVehicleIds={
                                selectedEligibleVehicleIds
                              }
                              toggleVehicleEligibility={
                                toggleVehicleEligibility
                              }
                              selectedEligibleThermostatIds={
                                selectedEligibleThermostatIds
                              }
                              toggleThermostatEligibility={
                                toggleThermostatEligibility
                              }
                              selectedEligibleChargerIds={
                                selectedEligibleChargerIds
                              }
                              toggleChargerEligibility={
                                toggleChargerEligibility
                              }
                              onAddDevice={onAddDevice}
                              deviceIncentives={deviceIncentives}
                              selectedDevicesMeetLimits={
                                selectedDevicesMeetLimits
                              }
                              rewardDeviceLimits={rewardDeviceLimits}
                            />
                          ),
                        )
                        .with(
                          { deviceIneligibility: P.nullish },
                          () => loadingAnimation,
                        )
                        .otherwise(() => null)}
                    </>
                  }
                />
              </div>
            </Tabs.Content>
          </section>
        )}
      </div>
      <div className="flex flex-col gap-4">
        {deviceLimitsResult.limitsMet && (
          <AddDeviceButton
            onAddDevice={onAddDevice}
            noDevicesAtAll={noDevicesAtAll}
          />
        )}
        {match(selectedTabName)
          .with(TabName.INELIGIBLE, () => (
            <Button
              id="next-devices-button"
              onClick={() => {
                Tabs.setActiveTabIndex(TAB_KEY, 1)
                setTabIndex(1)
              }}
              variant="primary"
            >
              Next
            </Button>
          ))
          .with(TabName.ELIGIBLE, () => (
            <Button
              id="enroll-devices-button"
              disabled={selectedDeviceCount === 0 || !selectedDevicesMeetLimits}
              onClick={onContinueClick}
            >
              Continue
            </Button>
          ))
          .exhaustive()}
      </div>
    </div>
  )
}

function TabContentRoot(props: { body: ReactNode }) {
  return (
    <div className="w-full justify-center border-solid rounded-md flex flex-col border border-themed-grey-200">
      <div className="py-4">{props.body}</div>
    </div>
  )
}

type IneligibleDevicesProps = {
  ineligibleDeviceCount: number
  deviceIneligibility: DeviceIneligibility
  onAddDevice: () => void
}

function IneligibleDevices({
  deviceIneligibility,
  ineligibleDeviceCount,
  onAddDevice,
}: IneligibleDevicesProps) {
  if (ineligibleDeviceCount === 0) {
    return <NoDevicesContent variant="Ineligible" onAddDevice={onAddDevice} />
  }
  return (
    <ul className="flex flex-col gap-4 px-2">
      {deviceIneligibility?.vehicles.map((v) => (
        <li key={v.id}>
          <Text variant="body1" className="flex items-center">
            <DotIcon /> {v.vehicle.car.car_model?.year}{' '}
            {v.vehicle.car.car_model?.friendly_name}
          </Text>
          <div className="px-6">
            {match({
              vehicleMake: v.vehicle.car.car_model?.make,
              requirements: v.requirements.map((r) => r.key),
            })
              .with(
                {
                  vehicleMake: 'tesla',
                  requirements: P.when((r) =>
                    r.includes(
                      VehicleEligibilityRequirement.HAS_READ_PERMISSIONS,
                    ),
                  ),
                },
                () => (
                  <div>
                    <Text variant="body2">
                      Permissions to read vehicle data are required to enroll in
                      this program.
                    </Text>
                    <a
                      href={`/devices/vehicle/${v.vehicle.id}/capability`}
                      className="!underline font-bold text-sm text-themed-blue-500 p-0 w-fit"
                      id={`device-select-edit-permissions-${v.vehicle.id}`}
                    >
                      Edit Permissions
                    </a>
                  </div>
                ),
              )
              .otherwise(() => (
                <Text variant="body2"> {v.requirements?.[0].content}</Text>
              ))}
          </div>
        </li>
      ))}
      {deviceIneligibility?.thermostats.map((v) => (
        <li key={v.id}>
          <Text variant="body2" className="flex items-center">
            <DotIcon /> {v.thermostat.name}
          </Text>
          <Text variant="body2" className="px-6">
            {v.requirements?.[0].content}
          </Text>
        </li>
      ))}
      {deviceIneligibility?.chargers.map((v) => (
        <li key={v.id}>
          <Text variant="body2" className="flex items-center">
            <DotIcon /> {v.charger.name}
          </Text>
          <Text variant="body2" className="px-6">
            {v.requirements?.[0].content}
          </Text>
        </li>
      ))}
    </ul>
  )
}

function EligibleDevices({
  selectedEligibleVehicleIds,
  toggleVehicleEligibility,
  selectedEligibleThermostatIds,
  toggleThermostatEligibility,
  selectedEligibleChargerIds,
  toggleChargerEligibility,
  deviceEligibility,
  eligibleDeviceCount,
  deviceIncentives,
  selectedDevicesMeetLimits,
  rewardDeviceLimits,
}: {
  selectedEligibleVehicleIds: number[]
  toggleVehicleEligibility: (id: number) => void
  selectedEligibleThermostatIds: number[]
  toggleThermostatEligibility: (id: number) => void
  selectedEligibleChargerIds: number[]
  toggleChargerEligibility: (id: number) => void
  deviceEligibility: DeviceEligibility
  eligibleDeviceCount: number
  onAddDevice: () => void
  deviceIncentives: UseViewModelProps['deviceIncentives']
  selectedDevicesMeetLimits: boolean
  rewardDeviceLimits: DeviceLimits
}) {
  if (eligibleDeviceCount === 0) {
    return <NoDevicesContent variant="Eligible" />
  }

  // A list containing selected eligible devices, capped at the max number of devices allowed for rewards
  const selectedIds = [
    ...selectedEligibleVehicleIds,
    ...selectedEligibleThermostatIds,
    ...selectedEligibleChargerIds,
  ]

  const allowedSelections =
    selectedIds.slice(0, rewardDeviceLimits.total_devices?.max ?? Infinity) ??
    []

  const rewardLimitsMet = DeviceLimitHelpers.UserWithinDeviceLimits(
    selectedEligibleVehicleIds?.length ?? 0,
    selectedEligibleThermostatIds?.length ?? 0,
    selectedEligibleChargerIds?.length ?? 0,
    0,
    false, // There is no behavioral device selection at this point
    rewardDeviceLimits,
  ).limitsMet

  const elligibleDevicesOverLimit =
    eligibleDeviceCount > (rewardDeviceLimits.total_devices?.max ?? Infinity)

  const displayRewardsAlert = !rewardLimitsMet || elligibleDevicesOverLimit

  const shouldDisplayIncentive = (id: number, deviceType: DeviceType) => {
    const selectedDevicesOfType = match(deviceType)
      .with('vehicle', () => selectedEligibleVehicleIds.length)
      .with('thermostat', () => selectedEligibleThermostatIds.length)
      .with('vehicleCharger', () => selectedEligibleChargerIds.length)
      .with('energySystem', () => 0)
      .with('home', () => 0)
      .exhaustive()

    const spaceRemaining = DeviceLimitHelpers.SpaceRemainingForDevice(
      deviceType,
      selectedDevicesOfType,
      selectedIds.length,
      rewardDeviceLimits,
    )

    return allowedSelections.includes(id) || spaceRemaining > 0
  }

  return (
    <div className="flex flex-col gap-4 px-4">
      {deviceEligibility?.vehicles?.map((v) => (
        <div
          key={v.id}
          className="items-center flex flex-row gap-4 cursor-pointer w-fit rounded-md -m-2 p-2"
          onClick={() => toggleVehicleEligibility(v.id)}
        >
          <Checkbox checked={selectedEligibleVehicleIds.includes(v.id)} />
          <Text variant="body2" className="mt-0.5">
            {getVehicleDisplayName(v.vehicle, true)}
          </Text>
          {shouldDisplayIncentive(v.id, 'vehicle') && (
            <IncentiveChip
              amount={deviceIncentives.vehicle}
              isSelected={selectedEligibleVehicleIds.includes(v.id)}
            />
          )}
        </div>
      ))}
      {deviceEligibility?.thermostats?.map((t) => (
        <div
          key={t.id}
          className="items-center flex flex-row gap-4 cursor-pointer w-fit rounded-md -m-2 p-2"
          onClick={() => toggleThermostatEligibility(t.id)}
        >
          <Checkbox checked={selectedEligibleThermostatIds.includes(t.id)} />
          <Text variant="body2">{t.thermostat.name}</Text>
          {shouldDisplayIncentive(t.id, 'thermostat') && (
            <IncentiveChip
              amount={deviceIncentives.thermostat}
              isSelected={selectedEligibleThermostatIds.includes(t.id)}
            />
          )}
        </div>
      ))}
      {deviceEligibility?.chargers?.map((c) => (
        <div
          key={c.id}
          className="items-center flex flex-row gap-4 cursor-pointer w-fit rounded-md -m-2 p-2"
          onClick={() => toggleChargerEligibility(c.id)}
        >
          <Checkbox checked={selectedEligibleChargerIds.includes(c.id)} />
          <Text variant="body2">{c.charger.name ?? 'Chargepoint Charger'}</Text>
          {shouldDisplayIncentive(c.id, 'vehicleCharger') && (
            <IncentiveChip
              amount={deviceIncentives.charger}
              isSelected={selectedEligibleChargerIds.includes(c.id)}
            />
          )}
        </div>
      ))}
      <ExpandFade
        animationKey="device-limits-alerts"
        show={!selectedDevicesMeetLimits || displayRewardsAlert}
      >
        <div className="flex flex-col gap-4">
          <DeviceLimitsAlert />
          <RewardLimitsAlert />
        </div>
      </ExpandFade>
    </div>
  )
}

function AddDeviceButton({
  onAddDevice,
  maxRewardForDevice,
  totalAwardedDollars,
  maximumRewardAmount,
  noDevicesAtAll,
}: {
  onAddDevice: () => void
  maxRewardForDevice?: number
  totalAwardedDollars?: number
  maximumRewardAmount?: number
  noDevicesAtAll?: boolean
}) {
  const badgeContent = match({
    maximumRewardAmount,
    maxRewardForDevice,
  })
    .with(
      {
        maximumRewardAmount: P.when(
          (max) =>
            max !== undefined &&
            totalAwardedDollars !== undefined &&
            totalAwardedDollars < max,
        ),
        maxRewardForDevice: P.nonNullable,
      },
      ({ maxRewardForDevice }) => (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2, ease: 'easeIn' }}
        >
          <IncentiveChip
            prefix="Up to"
            amount={maxRewardForDevice as number}
            isSelected={false}
          />
        </motion.div>
      ),
    )
    .otherwise(() => null)

  return (
    <Button
      id="add-device-button"
      variant="primary-subtle"
      onClick={onAddDevice}
    >
      {`Connect ${noDevicesAtAll ? 'a' : 'Another'} Device`}
      <AnimatePresence>{badgeContent}</AnimatePresence>
    </Button>
  )
}

function NoDevicesContent(props: {
  variant: 'Eligible' | 'Ineligible'
  onAddDevice?: () => void
}) {
  const message = match(props.variant)
    .with('Eligible', () => 'No eligible devices. Add devices to earn rewards!')
    .with('Ineligible', () => 'No ineligible devices')
    .exhaustive()
  return (
    <div className="flex flex-col gap-4 px-2 justify-center items-center">
      <Text className="text-center" variant={'body1'}>
        {message}
      </Text>
      <Icon name={'WholeHome'} />
    </div>
  )
}

function NoDevicesAtAllContent() {
  return (
    <div className="flex flex-col gap-4 px-2 justify-center items-center">
      <Text className="text-center" variant={'body1'}>
        You don't have any devices connected
      </Text>
      <Icon name={'WholeHome'} />
    </div>
  )
}

export function IncentiveChip(props: {
  prefix?: string
  amount: number
  isSelected: boolean
}) {
  if (props.amount === 0) {
    return null
  }

  return (
    <div
      className={`rounded-lg px-2 ${
        props.isSelected
          ? 'bg-themed-green-100 text-themed-green-900'
          : 'bg-themed-grey-100 text-themed-grey-500'
      } font-bold text-sm`}
    >
      {props.prefix} ${props.amount}
    </div>
  )
}

function DeviceLimitsAlert() {
  const { utilityProgram } = useMultiProgramRouteContext()
  const deviceLimits = useAppSelector(
    selectProgramDeviceLimits(utilityProgram.id),
  )
  const limitsText = DeviceLimitHelpers.DeviceLimitsAlertText(deviceLimits)

  if (!limitsText) {
    return null
  }

  return (
    <AlertMessage variant="info">
      You must select <strong>{limitsText}</strong> to apply for this program
    </AlertMessage>
  )
}

function RewardLimitsAlert() {
  const { utilityProgram } = useMultiProgramRouteContext()
  const rewardDeviceLimits = useAppSelector(
    selectProgramRewardDeviceLimits(utilityProgram.id),
  )
  const limitsText =
    DeviceLimitHelpers.DeviceLimitsAlertText(rewardDeviceLimits)

  if (!limitsText) {
    return null
  }

  return <MessageBox>Rewards only apply for {limitsText}</MessageBox>
}
